import { useState } from 'react';
import { Modal, ModalFooter } from '../ui/Modal';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Alert } from '../ui/Alert';
import { useRecoveryCode } from '../../api/two-factor';
import type { User } from '../../api/auth';

interface TwoFactorRecoveryModalProps {
  isOpen: boolean;
  challengeToken: string;
  expiresAt: string;
  onSuccess: (user: User, csrfToken: string, remainingCodes?: number) => void;
  onCancel: () => void;
  onUseAuthenticator: () => void;
}

export function TwoFactorRecoveryModal({
  isOpen,
  challengeToken,
  expiresAt,
  onSuccess,
  onCancel,
  onUseAuthenticator,
}: TwoFactorRecoveryModalProps) {
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const isExpired = new Date(expiresAt) < new Date();

  const formatCode = (value: string) => {
    const cleaned = value.toUpperCase().replace(/[^A-Z0-9]/g, '');
    if (cleaned.length <= 5) {
      return cleaned;
    }
    return `${cleaned.slice(0, 5)}-${cleaned.slice(5, 10)}`;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCode(formatCode(e.target.value));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      const response = await useRecoveryCode(challengeToken, code);
      onSuccess(response.user, response.csrfToken, response.remainingRecoveryCodes);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Invalid recovery code');
      setCode('');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setCode('');
    setError(null);
    onCancel();
  };

  const isValidFormat = /^[A-Z0-9]{5}-[A-Z0-9]{5}$/.test(code);

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Use recovery code" size="sm">
      <form onSubmit={handleSubmit} className="space-y-6">
        {isExpired ? (
          <Alert variant="error">
            Your verification session has expired. Please log in again.
          </Alert>
        ) : (
          <>
            <p className="text-body-sm text-text-secondary">
              Enter one of your recovery codes to access your account.
            </p>

            {error && (
              <Alert variant="error" onClose={() => setError(null)}>
                {error}
              </Alert>
            )}

            <Input
              label="Recovery code"
              type="text"
              value={code}
              onChange={handleChange}
              placeholder="XXXXX-XXXXX"
              maxLength={11}
              autoFocus
              required
              disabled={isExpired}
            />
          </>
        )}

        <div className="text-center">
          <button
            type="button"
            onClick={onUseAuthenticator}
            className="text-body-sm text-text-tertiary hover:text-text-primary underline"
            disabled={isExpired}
          >
            Use authenticator app instead
          </button>
        </div>

        <ModalFooter>
          <Button type="button" variant="outline" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            disabled={!isValidFormat || isLoading || isExpired}
          >
            {isLoading ? 'Verifying...' : 'Verify'}
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  );
}
